import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ArrowLeft, Eye, EyeOff } from "lucide-react"
import { useEffect, useState } from "react"
import { Link, useParams } from "react-router"

import { api } from "@/api/cliente"
import { Calificacion } from "@/componentes/evaluaciones/Calificacion"
import { Consolidado, PlanDesarrollo } from "@/componentes/evaluaciones/Consolidado"
import { documentoDeEvaluacion } from "@/componentes/evaluaciones/exportarEvaluacion"
import {
  ESTADO,
  type Evaluacion as Ev,
  type Notas,
  type Pdi,
} from "@/componentes/evaluaciones/tipos"
import { Exportar } from "@/componentes/exportar/Exportar"
import { Boton } from "@/componentes/ui/Boton"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { Insignia } from "@/componentes/ui/Insignia"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFecha } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

type Borrador = Pick<
  Ev,
  "notas_direccion" | "hechos" | "indicadores" | "acordadas" | "valoracion" | "pdi"
>

const TEXTOS_AUTO: Record<string, string> = {
  fortalezas: "Mis fortalezas",
  mejorar: "Lo que debo mejorar",
  obstaculos: "Obstáculos que encontré",
  metas: "Mis metas para el próximo trimestre",
}

/**
 * Una evaluación, del lado de la Dirección (28/10): partes 1 a 5 del formato de octubre.
 *
 * ⚠ **La autoevaluación está tapada hasta que se pida verla.** La guía del programa pide
 * calificar ANTES de leerla, para que la mirada de la Dirección sea independiente.
 */
export function Evaluacion() {
  const { id } = useParams()
  const usuario = usarSesion((e) => e.usuario)
  const puede = tienePermiso(usuario, "evaluacion:administrar")
  const clienteQuery = useQueryClient()
  const [b, setB] = useState<Borrador | null>(null)
  const [verAuto, setVerAuto] = useState(false)

  const consulta = useQuery({
    queryKey: ["evaluacion", id],
    queryFn: () => api.get<Ev>(`/api/v1/evaluaciones/${id}`),
  })
  useEffect(() => {
    if (consulta.data) {
      const d = consulta.data
      setB({
        notas_direccion: d.notas_direccion,
        hechos: d.hechos,
        indicadores: d.indicadores,
        acordadas: d.acordadas,
        valoracion: d.valoracion,
        pdi: d.pdi,
      })
    }
  }, [consulta.data])

  const listo = (ev: Ev) => {
    clienteQuery.setQueryData(["evaluacion", id], ev)
    clienteQuery.invalidateQueries({ queryKey: ["evaluaciones"] })
  }
  const guardar = useMutation({
    mutationFn: (cuerpo: Partial<Borrador>) => api.patch<Ev>(`/api/v1/evaluaciones/${id}`, cuerpo),
    onSuccess: listo,
  })
  const paso = useMutation({
    mutationFn: async (accion: "consolidar" | "cerrar" | "anular") => {
      if (b && ev?.estado !== "CERRADA")
        await api.patch(`/api/v1/evaluaciones/${id}`, camposDe(ev, b))
      return api.post<Ev>(`/api/v1/evaluaciones/${id}/${accion}`, {})
    },
    onSuccess: listo,
  })

  const ev = consulta.data
  if (consulta.isPending) return <EstadoCarga etiqueta="Cargando la evaluación" />
  if (consulta.isError || !ev) {
    return (
      <EstadoError
        titulo="No se pudo cargar la evaluación"
        error={consulta.error}
        onReintentar={() => consulta.refetch()}
      />
    )
  }
  if (!b) return null

  const abierta = ev.estado === "ABIERTA"
  const enSesion = ev.estado === "ABIERTA" || ev.estado === "CONSOLIDADA"
  const error = guardar.error ?? paso.error

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <Link
        to="/talento-humano/evaluaciones"
        className="foco-cristal inline-flex items-center gap-1 text-sm"
      >
        <ArrowLeft size={16} /> Evaluaciones
      </Link>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Evaluación del desempeño · {ev.persona}</h1>
          <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
            {ev.plantilla.nombre} · {comoFecha(ev.desde)} al {comoFecha(ev.hasta)}
          </p>
        </div>
        <div className="flex flex-wrap items-start gap-2">
          <Insignia tono={ev.estado === "CERRADA" ? "exito" : "info"}>{ESTADO[ev.estado]}</Insignia>
          <Exportar
            titulo={`Evaluación del desempeño · ${ev.persona}`}
            documento={() => documentoDeEvaluacion(ev)}
          />
        </div>
      </header>

      {error && (
        <p role="alert" className="tarjeta p-3 text-sm" style={{ color: "var(--destructive)" }}>
          {(error as Error).message}
        </p>
      )}

      <section className="space-y-2">
        <h2 className="text-base font-semibold">Parte 1 · Indicadores del período</h2>
        <div className="space-y-2">
          {ev.plantilla.indicadores.map((ind, i) => {
            const v = b.indicadores[String(i)] ?? {}
            const poner = (k: "resultado" | "comentario", t: string) =>
              setB({ ...b, indicadores: { ...b.indicadores, [String(i)]: { ...v, [k]: t } } })
            return (
              <div key={ind.texto} className="tarjeta grid gap-2 p-3 sm:grid-cols-[2fr_1fr_1fr]">
                <div className="text-sm">
                  {ind.texto}
                  <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                    Fuente: {ind.fuente}
                  </span>
                </div>
                <input
                  aria-label={`Resultado: ${ind.texto}`}
                  placeholder="Resultado"
                  className="campo"
                  disabled={!abierta || !puede}
                  value={v.resultado ?? ""}
                  onChange={(e) => poner("resultado", e.target.value)}
                />
                <input
                  aria-label={`Comentario: ${ind.texto}`}
                  placeholder="Comentario"
                  className="campo"
                  disabled={!abierta || !puede}
                  value={v.comentario ?? ""}
                  onChange={(e) => poner("comentario", e.target.value)}
                />
              </div>
            )
          })}
        </div>
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-semibold">Parte 2 · Calificación de la Dirección</h2>
        <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
          Hechos del período, no impresiones generales. Si algo no se pudo observar: N/O, que no
          cuenta para el promedio.
        </p>
        <Calificacion
          prefijo="dir"
          formulario={ev.plantilla}
          notas={b.notas_direccion}
          onNotas={(n: Notas) => setB({ ...b, notas_direccion: n })}
          hechos={b.hechos}
          onHechos={(h) => setB({ ...b, hechos: h })}
          soloLectura={!abierta || !puede}
        />
      </section>

      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-base font-semibold">Autoevaluación del trabajador</h2>
          <Boton tamano="sm" onClick={() => setVerAuto((v) => !v)}>
            {verAuto ? <EyeOff size={14} /> : <Eye size={14} />}{" "}
            {verAuto ? "Ocultar" : "Ver la autoevaluación"}
          </Boton>
        </div>
        {!verAuto ? (
          <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
            {ev.auto_enviada_en ? "Entregada. " : "Todavía no la entrega. "}
            La guía del programa pide calificar antes de leerla, para que la mirada de la Dirección
            sea independiente.
          </p>
        ) : (
          <div className="space-y-3">
            <Calificacion
              prefijo="auto"
              formulario={ev.plantilla}
              notas={ev.notas_auto}
              onNotas={() => {}}
              soloLectura={true}
            />
            <dl className="tarjeta grid gap-2 p-4 text-sm">
              {Object.entries(TEXTOS_AUTO).map(([k, t]) => (
                <div key={k}>
                  <dt className="font-medium">{t}</dt>
                  <dd>{ev.autoevaluacion[k] || "—"}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}
      </section>

      {ev.resultado && (
        <section className="space-y-2">
          <h2 className="text-base font-semibold">Parte 3 · Consolidado (sesión 1 a 1)</h2>
          <Consolidado
            r={ev.resultado}
            acordadas={b.acordadas}
            onAcordada={
              enSesion && puede
                ? (k, v) => setB({ ...b, acordadas: { ...b.acordadas, [k]: v } })
                : undefined
            }
          />
          <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
            La nota acordada es por omisión la de la Dirección; se ajusta hacia la del trabajador
            sólo si aporta evidencia que la Dirección no tenía. Guarda para recalcular.
          </p>
        </section>
      )}

      <section className="space-y-2">
        <h2 className="text-base font-semibold">Parte 4 · Valoración de la Dirección</h2>
        {(
          [
            ["fortalezas", "Fortalezas reconocidas"],
            ["mejoras", "Áreas de mejora prioritarias (máximo tres)"],
            ["brechas", "Bloques con brecha mayor que 1,0: casos revisados y conclusión"],
          ] as const
        ).map(([k, t]) => (
          <label key={k} className="block text-sm">
            {t}
            <textarea
              className="campo mt-1"
              rows={2}
              disabled={!enSesion || !puede}
              value={b.valoracion[k] ?? ""}
              onChange={(e) => setB({ ...b, valoracion: { ...b.valoracion, [k]: e.target.value } })}
            />
          </label>
        ))}
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-semibold">Parte 5 · Plan de Desarrollo Individual (PDI)</h2>
        <PlanDesarrollo
          pdi={b.pdi}
          onCambiar={enSesion && puede ? (p: Pdi) => setB({ ...b, pdi: p }) : undefined}
        />
      </section>

      {ev.observaciones_trabajador && (
        <section className="space-y-1">
          <h2 className="text-base font-semibold">Parte 6 · Observaciones del trabajador</h2>
          <p className="tarjeta p-3 text-sm">{ev.observaciones_trabajador}</p>
        </section>
      )}

      {puede && ev.estado !== "CERRADA" && ev.estado !== "ANULADA" && (
        <div
          className="flex flex-wrap gap-2 border-t pt-4"
          style={{ borderColor: "var(--border)" }}
        >
          <Boton
            variante="primario"
            disabled={guardar.isPending}
            onClick={() => guardar.mutate(camposDe(ev, b))}
          >
            {guardar.isPending ? "Guardando…" : "Guardar"}
          </Boton>
          {abierta && (
            <Boton disabled={paso.isPending} onClick={() => paso.mutate("consolidar")}>
              Consolidar (sesión hecha)
            </Boton>
          )}
          {ev.estado === "CONSOLIDADA" && (
            <Boton disabled={paso.isPending} onClick={() => paso.mutate("cerrar")}>
              Cerrar con el PDI firmado
            </Boton>
          )}
          <Boton variante="peligro" disabled={paso.isPending} onClick={() => paso.mutate("anular")}>
            Anular
          </Boton>
        </div>
      )}
    </div>
  )
}

/** Lo que se puede guardar en el estado actual: las notas sólo abierta. */
function camposDe(ev: Ev | undefined, b: Borrador): Partial<Borrador> {
  const sesion = { acordadas: b.acordadas, valoracion: b.valoracion, pdi: b.pdi }
  if (ev?.estado === "ABIERTA") {
    return {
      ...sesion,
      notas_direccion: b.notas_direccion,
      hechos: b.hechos,
      indicadores: b.indicadores,
    }
  }
  return sesion
}
